import * as z from "zod";
import { WhereCondition } from "../../segments/segment-where-condition-zod-schema";
import {
  SegmentDataStructureField,
  segmentDataStructureTypeToDataStructure,
} from "./segment-data-structures";
import { CustomerActivityType, SegmentConditionType } from "./segment-types";

type SegmentConditionInput =
  | {
      type: SegmentConditionType.CUSTOMER_ATTRIBUTE;
      whereCondition: WhereCondition;
    }
  | {
      type: SegmentConditionType.CUSTOMER_ACTIVITY;
      event: CustomerActivityType;
      event_filters: WhereCondition[];
    }
  | { type: SegmentConditionType.CUSTOM_EVENT };

export function refineSegmentCondition(
  condition: SegmentConditionInput,
  ctx: z.RefinementCtx,
): void {
  if (condition.type === SegmentConditionType.CUSTOMER_ATTRIBUTE) {
    refineWhereCondition(
      condition.whereCondition,
      segmentDataStructureTypeToDataStructure["customer-characteristic"].fields,
      ctx,
      ["whereCondition"],
      "customer attribute",
    );
    return;
  }
  if (condition.type !== SegmentConditionType.CUSTOMER_ACTIVITY) {
    return;
  }
  const fields =
    segmentDataStructureTypeToDataStructure[condition.event].fields;
  if (Object.keys(fields).length === 0) {
    if (condition.event_filters.length > 0) {
      ctx.addIssue({
        code: "custom",
        path: ["event_filters"],
        message: `Activity "${condition.event}" has no filter dimensions. Remove event_filters.`,
      });
    }
    return;
  }
  for (const [index, filter] of condition.event_filters.entries()) {
    refineWhereCondition(
      filter,
      fields,
      ctx,
      ["event_filters", index],
      `activity "${condition.event}" filter`,
    );
  }
}

function refineWhereCondition(
  condition: WhereCondition,
  fields: Record<string, SegmentDataStructureField<string>>,
  ctx: z.RefinementCtx,
  path: PropertyKey[],
  subject: string,
): void {
  const field = fields[condition.dimension];
  if (!field) {
    ctx.addIssue({
      code: "custom",
      path: [...path, "dimension"],
      message: `Unknown ${subject} dimension "${condition.dimension}". Valid values: ${Object.keys(fields).join(", ")}`,
    });
    return;
  }
  if (field.dataType !== condition.type) {
    ctx.addIssue({
      code: "custom",
      path: [...path, "type"],
      message: `"${condition.dimension}" is a ${field.dataType} dimension, not ${condition.type}`,
    });
  }
}
